import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PdfViewerModal } from "../components/PdfViewerModal";
import { ReferenceCheckCard } from "../components/ReferenceCheckCard";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const STATUS_META = {
  pending: { label: "Awaiting review", cls: "bg-[#B7791F]/10 text-[#8a5d14] border-[#B7791F]/30" },
  satisfactory: { label: "Satisfactory", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
  unsatisfactory: { label: "Not satisfactory", cls: "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30" },
};

export default function ReferencesPage() {
  const [refs, setRefs] = useState(null);
  const [missing, setMissing] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfView, setPdfView] = useState(null);
  const { user } = useAuth();
  const canDocument = user?.role === "admin" || (user?.scopes || []).includes("references");
  const [showDoc, setShowDoc] = useState(false);
  const [empList, setEmpList] = useState([]);
  const [docUid, setDocUid] = useState("");
  const [docCtx, setDocCtx] = useState(null);

  const openDocPicker = async () => {
    setShowDoc(true);
    if (!empList.length) {
      try {
        const { data } = await api.get("/users/nav-order");
        setEmpList(data.order || []);
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const pickEmployee = async (uid2) => {
    setDocUid(uid2);
    setDocCtx(null);
    if (!uid2) return;
    try {
      const { data } = await api.get(`/users/${uid2}/reference-context`);
      setDocCtx(data);
    } catch (e) { toast.error(formatApiError(e)); setDocUid(""); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/references");
      setRefs(data.references);
      setMissing(data.missing || []);
      setWaiting(data.waiting || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRefs([]);
    }
  };
  useEffect(() => { load(); }, []);

  const review = async (r, verdict) => {
    setBusy(true);
    try {
      await api.post(`/users/${r.user_id}/employment-records/${r.record_id}/reference-review`, { verdict, note });
      toast.success(`Marked ${verdict} — ${r.ref_name} for ${r.user_name}`);
      setExpanded(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (t) => {
    setBusy(true);
    try {
      await api.post(`/references/resend/${t.token}`);
      toast.success(`Reminder sent to ${t.ref_name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const closeRequest = async (t) => {
    setBusy(true);
    try {
      await api.post(`/references/close/${t.token}`);
      toast.success(`${t.ref_name} marked handled — chase stopped`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    if (!refs) return [];
    const q = query.toLowerCase();
    return refs.filter((r) =>
      (filter === "all" || r.review_status === filter) &&
      (!q || (r.user_name || "").toLowerCase().includes(q) || (r.ref_name || "").toLowerCase().includes(q) || (r.dcw_code || "").toLowerCase().includes(q)));
  }, [refs, filter, query]);

  const pending = (refs || []).filter((r) => r.review_status === "pending").length;
  const [backfillBusy, setBackfillBusy] = useState(false);

  const backfillContacts = async () => {
    setBackfillBusy(true);
    try {
      const { data } = await api.post("/admin/backfill-reference-contacts", {}, { timeout: 120000 });
      toast.success(`Filled reference contact fields on ${data.filled} profiles (${data.skipped} already complete or without filed checks)`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBackfillBusy(false);
  };

  return (
    <div className="max-w-6xl" data-testid="references-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">References</h1>
          <p className="mt-1 text-sm text-stone-500">
            Every reference check across all staff — emailed responses land here awaiting your review. Two satisfactory references satisfy the roster rule.
          </p>
        </div>
        <span className="flex items-center gap-2">
          {pending > 0 && (
            <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-3 py-1 text-xs font-semibold text-[#8a5d14]" data-testid="references-pending-count">
              {pending} awaiting review
            </span>
          )}
          <button onClick={backfillContacts} disabled={backfillBusy}
            className="rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-900 hover:text-white disabled:opacity-50"
            data-testid="references-backfill-button">
            {backfillBusy ? "Filling…" : "Fill contact fields from filed checks"}
          </button>
        </span>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="references-filters">
        {[["pending", "Awaiting review"], ["waiting", `Waiting (${waiting.length})`], ["satisfactory", "Satisfactory"], ["unsatisfactory", "Not satisfactory"], ["missing", `Missing (${missing.length})`], ["all", "All"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${filter === v ? "bg-primary text-primary-foreground" : "border border-stone-300 bg-white text-stone-600 hover:bg-stone-50"}`}
            data-testid={`references-filter-${v}`}>{l}</button>
        ))}
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search employee, reference, or DCW code…"
          className="h-9 min-w-64 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="references-search" />
        {canDocument && (
          <button onClick={openDocPicker}
            className="rounded-full bg-[#4A235A] px-4 py-1.5 text-xs font-semibold text-white hover:bg-stone-900"
            data-testid="references-document-btn">+ Document a reference check</button>
        )}
      </div>

      {filter === "waiting" && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="references-waiting-panel">
          <p className="border-b border-stone-100 px-4 py-3 text-xs text-stone-500">
            Emailed reference checks still out. The system sends one reminder on day 2 — if there's still nothing on day 3, the request is flagged <strong>call now</strong> and lands on the admin to-do list. Nudge anytime; mark it handled once you've called it in.
          </p>
          {waiting.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-stone-400">Nothing waiting — every emailed reference check has come back.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400">
                  <th className="px-4 py-2">Reference</th><th className="px-4 py-2">For</th><th className="px-4 py-2">Sent</th>
                  <th className="px-4 py-2">Attempts</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {waiting.map((t) => (
                  <tr key={t.token} className="border-b border-stone-100 last:border-0 hover:bg-accent/30" data-testid={`waiting-row-${t.token}`}>
                    <td className="px-4 py-3 font-medium text-stone-800" data-testid={`waiting-name-${t.token}`}>{t.ref_name}
                      {t.relationship && <span className="ml-1 text-xs text-stone-400">({t.relationship})</span>}</td>
                    <td className="px-4 py-3 text-stone-600" data-testid={`waiting-for-${t.token}`}>{t.applicant_name}</td>
                    <td className="px-4 py-3 text-xs text-stone-500" data-testid={`waiting-sent-${t.token}`}>{(t.sent_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-xs text-stone-600" data-testid={`waiting-attempts-${t.token}`}>
                      {t.attempts} sent · last {(t.last_attempt_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3" data-testid={`waiting-status-${t.token}`}>
                      {t.unresponsive
                        ? <span className="inline-block rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C0392B]">Day 3+ — call now</span>
                        : <span className="inline-block rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a5d14]">Waiting</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => resend(t)} disabled={busy}
                        className="mr-3 text-xs font-medium text-primary hover:underline disabled:opacity-50" data-testid={`waiting-resend-${t.token}`}>
                        Nudge now
                      </button>
                      <button onClick={() => closeRequest(t)} disabled={busy}
                        className="text-xs font-medium text-stone-500 hover:underline disabled:opacity-50" data-testid={`waiting-close-${t.token}`}>
                        Mark handled
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {filter === "missing" && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="references-missing-panel">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
              <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3">DCW code</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Interview</th><th className="px-4 py-3">ID on file</th><th className="px-4 py-3">Satisfactory references</th></tr>
            </thead>
            <tbody>
              {missing
                .filter((m) => !query || (m.user_name || "").toLowerCase().includes(query.toLowerCase()) || (m.dcw_code || "").toLowerCase().includes(query.toLowerCase()))
                .map((m) => (
                <tr key={m.user_id} className="border-b border-stone-100 last:border-0 hover:bg-accent/30" data-testid={`missing-row-${m.user_id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/team/${m.user_id}`} className="font-medium text-primary hover:underline" data-testid={`missing-employee-${m.user_id}`}>{m.user_name}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-600">{m.dcw_code || "—"}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{m.user_active ? "Active" : "Inactive"}</td>
                  <td className="px-4 py-3" data-testid={`missing-interview-${m.user_id}`}>
                    {m.interview_done
                      ? <span className="inline-block rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-semibold text-[#1e8449]">done</span>
                      : <span className="inline-block rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C0392B]">missing</span>}
                  </td>
                  <td className="px-4 py-3" data-testid={`missing-id-${m.user_id}`}>
                    {m.id_on_file
                      ? <span className="inline-block rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-semibold text-[#1e8449]">on file</span>
                      : <span className="inline-block rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C0392B]">missing</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${m.positive_count === 0 ? "bg-[#C0392B]/10 text-[#C0392B] border-[#C0392B]/30" : "bg-[#B7791F]/10 text-[#8a5d14] border-[#B7791F]/30"}`}
                      data-testid={`missing-count-${m.user_id}`}>{m.positive_count}/2{m.pending_count ? ` (+${m.pending_count} pending)` : ""}</span>
                  </td>
                </tr>
              ))}
              {missing.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-stone-400">Everyone has 2 satisfactory references.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {filter !== "missing" && (
      <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
            <tr>
              <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Recommends?</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const meta = STATUS_META[r.review_status] || STATUS_META.pending;
              const key = `${r.user_id}:${r.record_id}`;
              return [
                <tr key={key} className="border-b border-stone-100 last:border-0 hover:bg-accent/30" data-testid={`reference-row-${r.record_id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/team/${r.user_id}`} className="font-medium text-primary hover:underline" data-testid={`reference-employee-${r.record_id}`}>{r.user_name}</Link>
                    <span className="ml-1.5 text-xs text-stone-400">{r.dcw_code}</span>
                    {!r.user_active && <span className="ml-1.5 text-[10px] text-stone-400">(inactive)</span>}
                  </td>
                  <td className="px-4 py-3">{r.ref_name}<span className="ml-1.5 text-xs text-stone-400">{r.relationship}</span></td>
                  <td className="px-4 py-3 text-xs text-stone-600" data-testid={`reference-source-${r.record_id}`}>{r.entry_type === "legacy" ? "Legacy transcription" : r.source === "email" ? "Email response" : "Phone"}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{r.date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{r.recommend || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`} data-testid={`reference-status-${r.record_id}`}>{meta.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setExpanded(expanded === key ? null : key); setNote(""); }}
                      className="text-xs font-medium text-primary hover:underline" data-testid={`reference-expand-${r.record_id}`}>
                      {expanded === key ? "Close" : r.review_status === "pending" ? "Review" : "View"}
                    </button>
                  </td>
                </tr>,
                expanded === key && (
                  <tr key={`${key}-x`} className="border-b border-stone-100 bg-stone-50/60" data-testid={`reference-detail-${r.record_id}`}>
                    <td colSpan={7} className="px-4 py-3">
                      {r.summary && (
                        <p className="mb-2 rounded-md border border-stone-200 bg-white p-3 text-xs text-stone-700" data-testid={`reference-summary-${r.record_id}`}>
                          <span className="font-semibold text-stone-500">Summary: </span>{r.summary}
                        </p>
                      )}
                      {r.why_hire_text && (
                        <p className="mb-2 rounded-md border border-stone-200 bg-white p-3 text-xs text-stone-700">
                          <span className="font-semibold text-stone-500">Why we should / should not hire (verbatim): </span>{r.why_hire_text}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => setPdfView({ url: `${api.defaults.baseURL}/users/${r.user_id}/employment-records/${r.record_id}`, title: `Reference check — ${r.ref_name} for ${r.user_name}` })}
                          className="text-xs font-medium text-primary hover:underline" data-testid={`reference-pdf-${r.record_id}`}>View PDF proof</button>
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Your note (optional)"
                          className="h-8 min-w-48 flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`reference-note-${r.record_id}`} />
                        <Button onClick={() => review(r, "satisfactory")} disabled={busy} className="h-8 bg-[#27AE60] text-xs text-white hover:bg-[#1e8449]" data-testid={`reference-yes-${r.record_id}`}>Satisfactory — counts</Button>
                        <Button onClick={() => review(r, "unsatisfactory")} disabled={busy} className="h-8 bg-[#C0392B] text-xs text-white hover:bg-[#96281B]" data-testid={`reference-no-${r.record_id}`}>Not satisfactory</Button>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-400" data-testid="references-empty">
                {refs === null ? "Loading…" : "No references match this filter."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {showDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reference-document-modal">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg tracking-tight text-[#4A235A]">Document a reference check</p>
              <button onClick={() => { setShowDoc(false); setDocUid(""); setDocCtx(null); }}
                className="rounded-full border border-stone-300 px-3 py-1 text-xs text-stone-500 hover:bg-stone-50" data-testid="reference-document-close">Close</button>
            </div>
            <select value={docUid} onChange={(e) => pickEmployee(e.target.value)}
              className="mt-3 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="reference-document-employee">
              <option value="">Choose the employee…</option>
              {empList.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            {docUid && !docCtx && <p className="py-6 text-center text-sm text-stone-400">Loading their file…</p>}
            {docCtx && (
              <div className="mt-4">
                <ReferenceCheckCard uid={docUid} emp={docCtx.user} docs={docCtx.docs} isAdmin={user?.role === "admin"} canEdit
                  onChanged={() => { pickEmployee(docUid); load(); }} />
              </div>
            )}
          </div>
        </div>
      )}

      <PdfViewerModal url={pdfView?.url} title={pdfView?.title || ""} onClose={() => setPdfView(null)} />
    </div>
  );
}
